import { TableLoadingOverlay } from "@selector/shared/components";
import type { FirstDataRenderedEvent, ICellRendererParams, SelectionChangedEvent } from "ag-grid-community";
import {
  type GridReadyEvent,
  type IsRowValidDropPositionCallback,
  type RowDragCancelEvent,
  type RowDragEndEvent,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { use, useEffect, useReducer, useRef } from "react";
import { HStack, SymbolIcon, themeSelector, TriggerButton, TriggerButtonSize, TruncateText } from "widgets";

import { ColumnTemplatesContext } from "./column-templates/context.ts";
import { SELECTION_COLUMN_DEFINITION } from "./settings.ts";
import type { PendingDrop } from "./utils";
import {
  cleanUpSourceGroup,
  evaluateDropPosition,
  getOrderedSelectedColumnIds,
  mergeGroups,
  reparentRowsToSyntheticGroup,
  setSelection,
} from "./utils";

export interface ColumnRow {
  id: string;
  groupName?: string;
  isGroup?: boolean;
  parentId?: string;
  title: string;
  colId?: string;
  hide?: boolean;
  fixed?: boolean;
}

const areSelectionsEqual = (left: string[], right: string[]) => {
  return left.length === right.length && left.every((colId, index) => colId === right[index]);
};

export const ColumnSelectionGrid = () => {
  const { columnsRows, setGridApi, setColumnSelection, currentColumnSelection, gridApi } = use(ColumnTemplatesContext);
  // We use this ref to remember the intent of the drop between the validation callback
  // and the actual `rowDragEnd` event where we can mutate the grid data.
  const pendingDropRef = useRef<PendingDrop | null>(null);

  const pinnedTopRowData = columnsRows?.filter((p) => p.fixed);
  const movableRowData = columnsRows?.filter((p) => !p.fixed);

  const resetPendingDrop = () => {
    pendingDropRef.current = null;
  };

  useEffect(() => {
    const api = gridApi?.current;

    if (!api) {
      return;
    }

    const desiredIds = currentColumnSelection.filter((colId): colId is string => !!colId);
    const currentSelectedIds = getOrderedSelectedColumnIds(api);

    if (areSelectionsEqual(desiredIds, currentSelectedIds)) {
      return;
    }

    setSelection(gridApi, desiredIds);
  }, [gridApi, currentColumnSelection, columnsRows]);

  const isRowValidDropPosition: IsRowValidDropPositionCallback<ColumnRow> = ({
    newParent,
    rows,
    moved,
    position,
    target,
    overNode,
  }) => {
    resetPendingDrop();

    const { response, pendingDrop } = evaluateDropPosition({
      moved,
      rows,
      newParentData: newParent?.data,
      position,
      targetNode: target,
      overNode,
    });

    pendingDropRef.current = pendingDrop;
    return response;
  };

  const handleGridReady = (params: GridReadyEvent<ColumnRow>) => {
    setGridApi?.(params.api);
  };

  const handleSelectionChange = (event: SelectionChangedEvent<ColumnRow> | RowDragEndEvent<ColumnRow>) => {
    const selectedIds = getOrderedSelectedColumnIds(event.api);
    setColumnSelection((prev) => (areSelectionsEqual(prev, selectedIds) ? prev : selectedIds));
  };

  const handleRowDragEnd = (event: RowDragEndEvent<ColumnRow>) => {
    const pendingDrop = pendingDropRef.current;
    resetPendingDrop();

    if (!pendingDrop) {
      handleSelectionChange(event);
      return;
    }

    const rowsDrop = event.rowsDrop;

    if (!rowsDrop) {
      return;
    }

    if (pendingDrop.mode === "group-drop") {
      cleanUpSourceGroup(pendingDrop.originalParentId, pendingDrop.originalParentData, event.api);
      handleSelectionChange(event);
      return;
    }

    if (pendingDrop.mode === "merge-groups") {
      mergeGroups(event.api, rowsDrop, pendingDrop);
      handleSelectionChange(event);
      return;
    }

    const newParentNode = rowsDrop.newParent ?? rowsDrop.target?.parent;
    const droppingToRoot = !newParentNode?.data;

    if (!droppingToRoot) {
      cleanUpSourceGroup(pendingDrop.originalParentId, pendingDrop.originalParentData, event.api);
      handleSelectionChange(event);
      return;
    }

    reparentRowsToSyntheticGroup(event.api, rowsDrop, pendingDrop);
    handleSelectionChange(event);
  };

  const handleRowDragCancel = (_event: RowDragCancelEvent<ColumnRow>) => {
    resetPendingDrop();
  };

  const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
    const initialSelection = currentColumnSelection.filter((colId): colId is string => !!colId);
    setSelection(gridApi, initialSelection);

    setTimeout(() => {
      params.api.sizeColumnsToFit();
    }, 0);
  };

  return (
    <div style={{ flex: 1, maxHeight: "100%", overflow: "hidden" }}>
      <AgGridReact<ColumnRow>
        rowData={movableRowData}
        pinnedTopRowData={pinnedTopRowData}
        rowDragEntireRow
        loading={columnsRows == null}
        loadingOverlayComponent={TableLoadingOverlay}
        columnDefs={[]}
        selectionColumnDef={SELECTION_COLUMN_DEFINITION}
        autoGroupColumnDef={{
          headerName: "Columns and Groups",
          field: "title",
          cellRendererSelector: (params) => {
            if (params.node.group || params.data?.isGroup) {
              return { component: CustomGroupCell, params };
            }

            return { component: CustomCell, params };
          },
          rowDrag: true,
          minWidth: 350,
          filter: "agTextColumnFilter",
          sortable: false,
          suppressHeaderContextMenu: true,
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true,
          suppressFloatingFilterButton: true,
        }}
        getRowId={({ data }) => data.id}
        onGridReady={handleGridReady}
        autoSizeStrategy={{ type: "fitGridWidth" }}
        treeData={true}
        treeDataParentIdField={"parentId"}
        groupDefaultExpanded={0}
        rowDragManaged={true}
        suppressMoveWhenRowDragging={true}
        suppressContextMenu
        theme={themeSelector.withParams({
          wrapperBorder: { width: 0 },
          selectedRowBackgroundColor: "unset",
        })}
        rowDragMultiRow={false}
        rowSelection={{
          mode: "multiRow",
          groupSelects: "descendants",
          checkboxLocation: "selectionColumn",
        }}
        isRowValidDropPosition={isRowValidDropPosition}
        onRowDragEnd={handleRowDragEnd}
        onRowDragCancel={handleRowDragCancel}
        onSelectionChanged={handleSelectionChange}
        onFirstDataRendered={onFirstDataRendered}
      />
      <style>{`
        .ag-floating-filter-input .ag-text-field-input:focus {
          outline: 0;
          box-shadow: none;
          border-color: var(--ag-border-color);
        }
      `}</style>
    </div>
  );
};

const CustomGroupCell = (params: ICellRendererParams<ColumnRow>) => {
  const [, forceUpdate] = useReducer((n) => n + 1, 0);
  const node = params.node;
  const hasChildren = (node.childrenAfterGroup?.length ?? 0) > 0 || (node.allChildrenCount ?? 0) > 0;

  useEffect(() => {
    const onExpand = () => forceUpdate();
    params.node.addEventListener("expandedChanged", onExpand);
    return () => params.node.removeEventListener("expandedChanged", onExpand);
  }, [params.node]);

  const handleToggle = () => {
    node.setExpanded(!node.expanded);
  };

  return (
    <HStack style={hasChildren ? {} : undefined}>
      <TruncateText text={params.value} style={{ fontWeight: 500, color: "var(--primary-main)", flex: 1 }} />
      {hasChildren && (
        <TriggerButton
          icon={node.expanded ? "unfold_less" : "unfold_more"}
          aria-expanded={!!node.expanded}
          onPress={handleToggle}
          label=""
          size={TriggerButtonSize.Small}
        />
      )}
    </HStack>
  );
};

const CustomCell = (params: ICellRendererParams<ColumnRow>) => {
  if (params.data?.fixed) {
    return (
      <HStack>
        <SymbolIcon icon="lock" size={16} />
        <TruncateText text={params.value} />
      </HStack>
    );
  }

  return (
    <div style={{ marginLeft: params.data?.parentId ? 20 : 0 }}>
      <TruncateText text={params.value} />
    </div>
  );
};
